import { DOCUMENT } from '@angular/common';
import { Component, ElementRef, Inject, Injector, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { merge, Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AppComponentBase } from '@shared/common/app-component-base';
import { DataService } from '@app/shared/common/auth/data.service';
import { UserSignalrService } from '@app/shared/layout/change-status/user-signalr.service';
import { AppConsts } from '@shared/commonHelper/AppConsts';
import {
    AddMyGrantInput,
    CalendarAccessGrantDto,
    CalendarBookingMode,
    CalendarVisibility,
    CommonLookupServiceProxy,
    ConnectedAppsServiceProxy,
    EmailAutomationServiceProxy,
    EmailAutomationSettingsServiceProxy,
    LookUpTables,
    LookupTableOutputDto,
    MyCalendarSettingsDto,
    MyCalendarSettingsServiceProxy,
    SenderMemoryDto,
    SetMyCalendarSyncInput,
    SetMySyncToMyDayInput,
    SetMyVisibilityInput,
    UserCalendarAccessDto,
    UserEmailAutomationSettingsDto,
} from '@shared/service-proxies/service-proxies';
import {
    CONNECTED_APPS_FOCUSABLE_SELECTOR,
    ConnectedAppsFocusTarget,
    connectedAppsFocusToRestore,
    connectedAppsTabWrapTarget,
    ConnectedAppsLoadState,
    connectedAppsInertAttr,
    emailProcessingSummary,
    forgotSenderToast,
    isConnectedAppsFocusTarget,
    M365LinkState,
    m365LinkStateOf,
    M365StatusView,
    m365StatusView,
} from './connected-apps-summary';

interface PickUser {
    id: number;
    name: string;
}

/** Precomputed grant-row view-model so the template binds fields, not method calls (STANDARDS: no logic in templates). */
interface GrantRowVm {
    grant: CalendarAccessGrantDto;
    initial: string;
    name: string;
    label: string;
    addedByText: string;
    canRemove: boolean;
}

/**
 * Part 7 §6 memory-management row (view-model). One remembered sender, precomputed so the template binds
 * fields only — the raw Disposition string is mapped to a friendly label + chip class here, never in the view.
 */
interface MemoryRowVm {
    id: number;
    email: string;
    domain: string;
    dispositionLabel: string;
    dispositionClass: string; // 'client' | 'lead' | 'muted' | 'flag' | 'watch'
    seenText: string;
    lastSeenText: string;
}

/**
 * "Connected apps" right-side drawer. Surfaces the user's Microsoft 365 connection (connect / reconnect /
 * disconnect) and their personal email-automation toggles. Connection state comes from
 * ConnectedAppsServiceProxy.getMicrosoft365Status (an active Graph token row exists); disconnect removes the
 * token; connect/reconnect reuse the existing OAuth consent flow (DataService.showTokenLink, with success
 * signalled back via DataService.isAccountLinked). The flag-to-task toggle writes a user-scoped ABP setting,
 * so it takes effect with no API restart. Self-contained fixed drawer to match the 600px design; exposes
 * show()/close() so AppComponent can drive it by @ViewChild like the profile modal.
 *
 * FU-Z4 x2: while open the drawer is a modal dialog (role="dialog", aria-modal, named by its title, tabindex="-1"):
 * show() moves the focus into it, Escape inside it closes it, and close() gives the focus back to the element that had
 * it at open when that element is still in the page. Closed, it stays inert and hidden (Z-4 FX-6, unchanged).
 */
@Component({
    selector: 'vf-connected-apps-modal',
    templateUrl: './connected-apps-modal.component.html',
    styleUrls: ['./connected-apps-modal.component.scss'],
})
export class ConnectedAppsModalComponent extends AppComponentBase implements OnInit, OnDestroy {
    isOpen = false;
    isLoading = false;
    /**
     * Z-4 FX-6: the connection starts UNKNOWN (it used to assume "linked"), so nothing says "Connected" before
     * GetMicrosoft365Status answers; `m365` is the precomputed row (connected-apps-summary.ts).
     */
    m365: M365StatusView = m365StatusView('unknown');
    /** Z-4 FX-6: the user's email settings load; "Email processing" says nothing until they arrive. */
    settingsState: ConnectedAppsLoadState = 'loading';
    /**
     * Z-4 FX-6 (OB-C-18, OB-A-01): the drawer stays mounted (AppComponent drives it), so while closed it is `inert`
     * (nothing inside takes the focus) and, once the slide-out has played, visibility:hidden (the .scss).
     */
    inertAttr: string | null = connectedAppsInertAttr(false);
    openPanel: string | null = null; // which settings row is expanded inline ('email' | 'sync' | 'visibility')
    actionsOpen = false; // gear-toggled Reconnect/Disconnect panel under the M365 row

    settings: UserEmailAutomationSettingsDto = new UserEmailAutomationSettingsDto();

    // ---- Part 7 §6: per-user sender memory (the memory-management panel) -------------------------
    memoryRows: MemoryRowVm[] = []; // remembered senders, precomputed for the 'memory' accordion panel
    memoryLoading = false; // spinner state for the memory list (loaded lazily when the panel opens)
    memoryLoaded = false; // avoid re-fetching every time the panel toggles open

    // ---- Calendar (E11) -------------------------------------------------------------------------
    calendar: MyCalendarSettingsDto = new MyCalendarSettingsDto();
    myAccess: UserCalendarAccessDto; // grants list shown inline under "Who can access your calendar"
    grantRows: GrantRowVm[] = []; // precomputed rows for the grants list (rebuilt whenever myAccess loads)
    accessUsers: PickUser[] = []; // teammates available to grant access to
    addPersonOpen = false; // the add-person picker inside the access panel
    readonly calendarV1Enabled = AppConsts.calendarV1FeatureEnabled;
    readonly visFull = CalendarVisibility._1;
    readonly visBusy = CalendarVisibility._2;
    readonly bookAnyone = CalendarBookingMode._1;
    readonly bookSpecific = CalendarBookingMode._2;

    /** FU-Z4 x2: the drawer element (the dialog), which takes the focus when it opens. */
    @ViewChild('drawer', { static: true }) drawerRef: ElementRef<HTMLElement>;

    private _linkSub: Subscription;
    private destroy$ = new Subject<void>();
    /** FU-Z4 x2: the element that had the focus when the drawer opened; null = none to give it back to. */
    private focusAtOpen: ConnectedAppsFocusTarget | null = null;
    /** FU-Z4 x2: the pending move of the focus into the drawer (after the view shows it); null = none. */
    private focusTimer: ReturnType<typeof setTimeout> | null = null;

    constructor(
        injector: Injector,
        private _settingsProxy: EmailAutomationSettingsServiceProxy,
        private _emailAutomation: EmailAutomationServiceProxy,
        private _connectedAppsProxy: ConnectedAppsServiceProxy,
        private _myCalProxy: MyCalendarSettingsServiceProxy,
        private _commonLookup: CommonLookupServiceProxy,
        private _dataService: DataService,
        private _userSignalr: UserSignalrService,
        @Inject(DOCUMENT) private _doc: Document
    ) {
        super(injector);
    }

    ngOnInit(): void {
        // The OAuth consent flow (triggered by showTokenLink, handled in mine.component) signals success here.
        this._linkSub = this._dataService.isAccountLinked.subscribe((linked) => {
            if (linked) {
                this.loadStatus();
                this.actionsOpen = false;
            }
        });

        // E10/E11 (live): when the admin changes the workspace visibility/booking policy (or a grant), the
        // open Calendar section must re-resolve — the personal visibility toggle's enabled/pinned state, the
        // "who can access" list gating, and the badges all depend on it. Refresh in place over SignalR so the
        // user never has to close + reopen the drawer (TC14). HTTP responses re-enter Angular's zone, so the
        // bound view updates without an explicit zone.run.
        if (this.calendarV1Enabled) {
            merge(this._userSignalr.calendarAccessUpdates, this._userSignalr.calendarFeatureUpdates)
                .pipe(takeUntil(this.destroy$))
                .subscribe((details) => {
                    if (details && this.appSession.tenantId !== details.tenantId) {
                        return;
                    }
                    if (this.isOpen) {
                        this.loadCalendar();
                        this.loadMyAccess();
                    }
                });
        }
    }

    ngOnDestroy(): void {
        if (this._linkSub) {
            this._linkSub.unsubscribe();
        }
        this.clearFocusTimer();
        this.focusAtOpen = null;
        this.destroy$.next();
        this.destroy$.complete();
    }

    show(): void {
        // FU-Z4 x2: remember the opener (not when already open: the focus is then inside the drawer).
        if (!this.isOpen) {
            this.rememberFocus();
        }
        this.isOpen = true;
        this.inertAttr = connectedAppsInertAttr(true);
        // FU-Z4 x2: the focus moves into the dialog once the view has shown it (no longer inert or hidden).
        this.focusDrawerSoon();
        this.actionsOpen = false;
        // Part 7 §6: reset the memory panel so a fresh open re-fetches (memory changes as reviews resolve).
        this.memoryLoaded = false;
        this.memoryRows = [];
        document.body.classList.add('connected-apps-open');
        // Z-4 FX-6: both summaries wait for their own answer on every open (never the last open's, nor a guess).
        this.setLinkState('unknown');
        this.loadSettings();
        this.loadStatus();
        if (this.calendarV1Enabled) {
            this.loadCalendar();
            this.loadMyAccess();
            this.loadAccessUsers();
        }
    }

    close(): void {
        const wasOpen: boolean = this.isOpen;

        this.clearFocusTimer();
        this.isOpen = false;
        this.inertAttr = connectedAppsInertAttr(false);
        document.body.classList.remove('connected-apps-open');
        // FU-Z4 x2: the focus goes back to the opener (however the drawer closed: ×, Escape or the backdrop).
        if (wasOpen) {
            this.restoreFocus();
        }
    }

    /**
     * FU-Z4 x2: Escape inside the open drawer closes it. The key stops here, so nothing behind the modal dialog hears it
     * (a confirm dialog lives outside the drawer and keeps its own Escape).
     */
    onDrawerEscape(event: Event): void {
        if (!this.isOpen) {
            return;
        }
        if (event) {
            event.stopPropagation();
        }
        this.close();
    }

    /**
     * FU-Z4-FIX FZ-3: Tab and Shift+Tab wrap inside the open modal drawer (aria-modal), so the focus never leaves it for the
     * page behind. The controls are read from the drawer's own element (visible ones only); the pure rule is
     * connectedAppsTabWrapTarget (connected-apps-summary.ts).
     */
    onDrawerTab(event: Event, shift: boolean): void {
        const drawer: HTMLElement | null = this.drawerRef ? this.drawerRef.nativeElement : null;
        const active: Element | null = this._doc ? this._doc.activeElement : null;
        let focusables: HTMLElement[] = [];
        let target: HTMLElement | null = null;

        if (!this.isOpen || !drawer) {
            return;
        }
        focusables = Array.from(drawer.querySelectorAll<HTMLElement>(CONNECTED_APPS_FOCUSABLE_SELECTOR)).filter(
            (element: HTMLElement) => element.offsetParent !== null
        );
        target = connectedAppsTabWrapTarget<HTMLElement>(focusables, active as HTMLElement | null, shift);
        if (target) {
            event.preventDefault();
            target.focus({ preventScroll: true });
        }
    }

    /** FU-Z4 x2: remember the element that has the focus now (the opener), to give it back on close. */
    private rememberFocus(): void {
        const active: Element | null = this._doc ? this._doc.activeElement : null;

        this.focusAtOpen = isConnectedAppsFocusTarget(active) ? active : null;
    }

    /** FU-Z4 x2: give the focus back to the opener when it is still in the page (connected-apps-summary.ts). */
    private restoreFocus(): void {
        const body: unknown = this._doc ? this._doc.body : null;
        const target: ConnectedAppsFocusTarget | null = connectedAppsFocusToRestore<ConnectedAppsFocusTarget>(
            this.focusAtOpen,
            isConnectedAppsFocusTarget(body) ? body : null
        );

        this.focusAtOpen = null;
        if (target) {
            target.focus({ preventScroll: true });
        }
    }

    /** FU-Z4 x2: move the focus to the dialog after this change detection removes `inert` and shows the drawer. */
    private focusDrawerSoon(): void {
        this.clearFocusTimer();
        this.focusTimer = setTimeout(() => {
            this.focusTimer = null;
            if (this.isOpen && this.drawerRef && this.drawerRef.nativeElement) {
                this.drawerRef.nativeElement.focus({ preventScroll: true });
            }
        });
    }

    private clearFocusTimer(): void {
        if (this.focusTimer !== null) {
            clearTimeout(this.focusTimer);
            this.focusTimer = null;
        }
    }

    get connectedEmail(): string {
        return this.appSession.user ? this.appSession.user.emailAddress : '';
    }

    // Summary shown collapsed next to "Email processing". Flag-to-task is independent of the master
    // toggle, so "flagged only" (master off, flagged on) is a real state. Z-4 FX-6: nothing until the
    // user's settings have loaded (an empty settings object used to read "Off").
    get emailSummary(): string {
        return emailProcessingSummary(this.settingsState, this.settings);
    }

    /** Accordion: open the clicked row's inline config panel, or close it if already open. */
    togglePanel(name: string): void {
        this.openPanel = this.openPanel === name ? null : name;
        // Part 7 §6: the sender-memory list is fetched lazily the first time its panel is opened.
        if (this.openPanel === 'memory' && !this.memoryLoaded) {
            this.loadMemory();
        }
    }

    toggleEmailProcessing(): void {
        // Z-4 FX-6: never persist a settings object that has not loaded (it would write both toggles off).
        if (this.settingsState !== 'loaded') {
            return;
        }
        // Master (Phase-2 automatic processing) and flag-to-task are INDEPENDENT: turning automatic
        // processing off leaves the flagged toggle untouched, so a user can keep flagging emails into
        // tasks while the pipeline is off. The worker gates them separately.
        this.settings.emailProcessingEnabled = !this.settings.emailProcessingEnabled;
        this.persist();
    }

    toggleFlagged(): void {
        if (this.settingsState !== 'loaded') {
            return;
        }
        this.settings.processFlaggedEmailsEnabled = !this.settings.processFlaggedEmailsEnabled;
        this.persist();
    }

    // ---- Part 7 §6: sender memory (view / correct-by-removal / reset) ----------------------------
    /** Collapsed-row summary next to "What Trovve remembers". */
    get memorySummary(): string {
        if (this.memoryLoading) {
            return '…';
        }
        const n = this.memoryRows.length;
        if (n === 0) {
            return this.memoryLoaded ? 'Nothing yet' : 'Manage';
        }
        return n === 1 ? '1 sender' : n + ' senders';
    }

    /** Load the current user's remembered senders (best-effort; the panel just shows empty on failure). */
    private loadMemory(): void {
        this.memoryLoading = true;
        this._emailAutomation
            .getUserSenderMemory()
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (result) => {
                    const rows = result && result.responseData ? result.responseData : [];
                    this.memoryRows = rows.map((r) => this.mapMemoryRow(r));
                    this.memoryLoaded = true;
                    this.memoryLoading = false;
                },
                () => {
                    this.memoryRows = [];
                    this.memoryLoaded = true;
                    this.memoryLoading = false;
                }
            );
    }

    /** Map a raw memory row to its view-model — the Disposition string becomes a friendly label + chip class HERE. */
    private mapMemoryRow(dto: SenderMemoryDto): MemoryRowVm {
        let label = 'Remembered';
        let cls = 'muted';
        switch (dto.disposition) {
            case 'client':
                label = 'Client';
                cls = 'client';
                break;
            case 'lead':
                label = 'Lead';
                cls = 'lead';
                break;
            case 'dismissed-not-lead':
                label = 'Not a lead';
                cls = 'muted';
                break;
            case 'vendor-noise':
                label = 'Vendor / noise';
                cls = 'muted';
                break;
            case 'watch':
                label = 'Watch';
                cls = 'watch';
                break;
            case 'spoofed':
                label = 'Flagged sender';
                cls = 'flag';
                break;
            case 'muted':
                // Part 7 §9: a 2nd dismiss mutes the sender (no task/banner even for strong mail). Heavier
                // neutral than "Not a lead" so an escalated mute reads distinctly; removable/resettable here.
                label = 'Muted';
                cls = 'mute';
                break;
            default:
                label = dto.disposition || 'Remembered';
                cls = 'muted';
                break;
        }
        const seen = dto.seenCount || 0;
        return {
            id: dto.id,
            email: dto.senderEmail || dto.senderDomain || '',
            domain: dto.senderDomain || '',
            dispositionLabel: label,
            dispositionClass: cls,
            seenText: seen <= 1 ? 'Seen once' : 'Seen ' + seen + ' times',
            lastSeenText: this.fmtMemoryDate(dto.lastSeenUtc),
        };
    }

    /** Correction-by-removal: forget one sender so the classifier stops treating it as trusted context. */
    removeMemory(row: MemoryRowVm): void {
        this._emailAutomation
            .removeSenderMemory(row.id)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                () => {
                    this.memoryRows = this.memoryRows.filter((r) => r.id !== row.id);
                    // FU-Z4 Z4-7 #1: the address is escaped (the toast renders HTML).
                    this.toastr.success(forgotSenderToast(row.email), null, this.toastrOptions);
                },
                () => this.toastr.error('Could not update memory', null, this.toastrOptions)
            );
    }

    /** Reset everything Trovve has learned for this user (the sender ledger AND the adaptive threshold). */
    resetMemory(): void {
        this.message.confirm(
            'This clears everything Trovve has learned about your senders and resets your review sensitivity to the firm default.',
            'Reset what Trovve remembers?',
            (confirmed: boolean) => {
                if (!confirmed) {
                    return;
                }
                this._emailAutomation
                    .resetUserMemory()
                    .pipe(takeUntil(this.destroy$))
                    .subscribe(
                        () => {
                            this.memoryRows = [];
                            this.memoryLoaded = true;
                            this.toastr.success('Memory reset', null, this.toastrOptions);
                        },
                        () => this.toastr.error('Could not reset memory', null, this.toastrOptions)
                    );
            }
        );
    }

    trackByMemoryRow(_i: number, row: MemoryRowVm): number {
        return row.id;
    }

    /** Format a last-seen timestamp regardless of whether NSwag typed it as a moment or a Date. */
    private fmtMemoryDate(d: any): string {
        if (!d) {
            return '';
        }
        try {
            if (typeof d.format === 'function') {
                return 'Last seen ' + d.format('MMM D, YYYY');
            }
            return 'Last seen ' + new Date(d).toLocaleDateString();
        } catch {
            return '';
        }
    }

    // ---- Calendar (E11) -------------------------------------------------------------------------
    /** The section shows only when the FE flag is on AND the workspace has Calendar enabled. */
    get showCalendar(): boolean {
        return this.calendarV1Enabled && !!this.calendar && this.calendar.featureEnabled;
    }

    /** A user may only tighten — Full is offered only when the workspace default is Full (§1.4). */
    get canChooseFull(): boolean {
        return !!this.calendar && this.calendar.canChooseFull;
    }

    get visibilitySummary(): string {
        return this.calendar && this.calendar.ownVisibility === this.visBusy ? 'Busy only' : 'Full details';
    }

    get calendarSyncSummary(): string {
        return this.calendar && this.calendar.calendarSyncEnabled ? 'On' : 'Off';
    }

    get syncSummary(): string {
        return this.calendar && this.calendar.syncToMyDay ? 'On' : 'Off';
    }

    get accessSummary(): string {
        // TC-024: in "Anyone on the team" booking mode there are no explicit per-user grants (grants only
        // exist in "Only specific people" mode), so a grant-count-only summary wrongly reads "No one" even
        // though the whole team can book + see. Short-circuit on the booking mode first — matches the
        // expanded panel's "Anyone on the team can book and see calendars" copy.
        if (this.calendar && this.calendar.bookingMode === this.bookAnyone) {
            return 'Anyone';
        }
        const n =
            this.myAccess && this.myAccess.grants
                ? this.myAccess.grants.length
                : this.calendar
                  ? this.calendar.accessGrantCount || 0
                  : 0;
        if (n === 0) {
            return 'No one';
        }
        return n === 1 ? '1 person' : n + ' people';
    }

    toggleCalendarSyncEnabled(): void {
        if (!this.calendar) {
            return;
        }
        this.calendar.calendarSyncEnabled = !this.calendar.calendarSyncEnabled;
        const input = new SetMyCalendarSyncInput();
        input.enabled = this.calendar.calendarSyncEnabled;
        this._myCalProxy
            .setMyCalendarSync(input)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                () => {
                    /* optimistic; kept */
                },
                () => {
                    this.loadCalendar();
                    this.toastr.error('Could not update Calendar sync', null, this.toastrOptions);
                }
            );
    }

    setMyVisibility(v: CalendarVisibility): void {
        if (!this.calendar || this.calendar.ownVisibility === v) {
            return;
        }
        if (v === this.visFull && !this.canChooseFull) {
            return; // can't loosen below the workspace default
        }
        this.calendar.ownVisibility = v;
        const input = new SetMyVisibilityInput();
        input.visibility = v;
        this._myCalProxy
            .setMyVisibility(input)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                () => {
                    /* optimistic; kept */
                },
                () => {
                    this.loadCalendar();
                    this.toastr.error('Could not update your calendar visibility', null, this.toastrOptions);
                }
            );
    }

    toggleSyncToMyDay(): void {
        if (!this.calendar) {
            return;
        }
        this.calendar.syncToMyDay = !this.calendar.syncToMyDay;
        const input = new SetMySyncToMyDayInput();
        input.enabled = this.calendar.syncToMyDay;
        this._myCalProxy
            .setMySyncToMyDay(input)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                () => {
                    /* optimistic; kept */
                },
                () => {
                    this.loadCalendar();
                    this.toastr.error('Could not update the My Day sync setting', null, this.toastrOptions);
                }
            );
    }

    // ---- Who can access (inline grants) ---------------------------------------------------------
    /** v9 §1.5: a calendar owner manages their own booking grants only when booking is "Only specific
     *  people". In "Anyone on the team" mode everyone can already book + see, so the list collapses to a
     *  muted line. */
    get showAccessList(): boolean {
        return !!this.calendar && this.calendar.bookingMode === this.bookSpecific;
    }

    // v9 §1.4: a grant always conveys booking + Full view — one badge, no level / see-only.
    private grantLabel(_g: CalendarAccessGrantDto): string {
        return 'Can see and book';
    }

    /** A user can't remove a grant their admin added. */
    private canRemoveGrant(g: CalendarAccessGrantDto): boolean {
        return !g.addedByAdmin;
    }

    get availableAccessUsers(): PickUser[] {
        const ownerId = this.myAccess ? this.myAccess.ownerUserId : null;
        const taken = new Set(
            (this.myAccess && this.myAccess.grants ? this.myAccess.grants : []).map((g) => g.grantedUserId)
        );
        return this.accessUsers.filter((u) => u.id !== ownerId && !taken.has(u.id));
    }

    addPerson(u: PickUser): void {
        this.addPersonOpen = false;
        const input = new AddMyGrantInput();
        input.grantedUserId = u.id;
        this._myCalProxy
            .addMyGrant(input)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                () => {
                    this.loadMyAccess();
                    this.loadCalendar();
                },
                () => this.toastr.error('Could not add the person', null, this.toastrOptions)
            );
    }

    removePerson(g: CalendarAccessGrantDto): void {
        if (!this.canRemoveGrant(g)) {
            return;
        }
        this._myCalProxy
            .removeMyGrant(g.id)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                () => {
                    this.loadMyAccess();
                    this.loadCalendar();
                },
                () => this.toastr.error('Could not remove the person', null, this.toastrOptions)
            );
    }

    trackByGrantRow(_i: number, row: GrantRowVm): number {
        return row.grant.id;
    }
    trackByUserId(_i: number, u: PickUser): number {
        return u.id;
    }

    private loadCalendar(): void {
        this._myCalProxy
            .getMySettings()
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (result) => {
                    this.calendar = result;
                },
                () => {
                    /* leave the section hidden if it fails */
                }
            );
    }

    private loadMyAccess(): void {
        this._myCalProxy
            .getMyAccess()
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (result) => {
                    this.myAccess = result;
                    this.rebuildGrantRows();
                },
                () => {
                    /* grants list stays empty on failure */
                }
            );
    }

    /** Precompute the grants list rows so the template binds fields, not per-CD method calls. */
    private rebuildGrantRows(): void {
        const grants = (this.myAccess && this.myAccess.grants) || [];
        this.grantRows = grants.map((g) => ({
            grant: g,
            initial: (g.grantedName || '?').charAt(0).toUpperCase(),
            name: g.grantedName || '',
            label: this.grantLabel(g),
            addedByText: this.canRemoveGrant(g) ? 'Added by you' : 'Added by your admin',
            canRemove: this.canRemoveGrant(g),
        }));
    }

    private loadAccessUsers(): void {
        this._commonLookup
            .getAllForLookupTable('', 0, 999999, '', false, '', LookUpTables._1, false, null)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (result: LookupTableOutputDto[]) => {
                    this.accessUsers = (result || []).map((u) => ({ id: u.id, name: u.name }));
                },
                () => {
                    /* picker stays empty */
                }
            );
    }

    // ---- Microsoft 365 connection actions -------------------------------------------------------
    // Connect (fresh) or after a disconnect: just run the OAuth consent flow; isAccountLinked refreshes status.
    connectMicrosoft(): void {
        this.actionsOpen = false;
        this._dataService.showTokenLink.next();
    }

    // Reconnect = disconnect, then re-run consent behind the scenes (clean re-auth).
    reconnect(): void {
        this.actionsOpen = false;
        this.isLoading = true;
        this._connectedAppsProxy
            .disconnectMicrosoft365()
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                () => {
                    this.isLoading = false;
                    this.setLinkState('not-connected');
                    this._dataService.showTokenLink.next();
                },
                () => {
                    this.isLoading = false;
                    this.toastr.error('Could not start reconnect', null, this.toastrOptions);
                }
            );
    }

    // Disconnect = just remove the token (hard-deleted server-side).
    disconnect(): void {
        this.actionsOpen = false;
        this.isLoading = true;
        this._connectedAppsProxy
            .disconnectMicrosoft365()
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                () => {
                    this.isLoading = false;
                    this.setLinkState('not-connected');
                    this.toastr.success('Microsoft 365 disconnected', null, this.toastrOptions);
                },
                () => {
                    this.isLoading = false;
                    this.toastr.error('Could not disconnect Microsoft 365', null, this.toastrOptions);
                }
            );
    }

    /**
     * Z-4 FX-6: the status check with an error branch. A failed check says so (and keeps Connect on offer) instead of
     * leaving the last state, or the old "linked" assumption, on screen.
     */
    private loadStatus(): void {
        this._connectedAppsProxy
            .getMicrosoft365Status()
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (result) => {
                    this.setLinkState(m365LinkStateOf(result));
                },
                () => {
                    this.setLinkState('failed');
                }
            );
    }

    /** The Microsoft 365 row follows the link state (connected-apps-summary.ts). */
    private setLinkState(state: M365LinkState): void {
        this.m365 = m365StatusView(state);
    }

    private loadSettings(): void {
        this.isLoading = true;
        this.settingsState = 'loading';
        this._settingsProxy
            .getUserSettings()
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (result) => {
                    this.settings = result || new UserEmailAutomationSettingsDto();
                    // No reconciliation: master (EmailProcessing) and flag-to-task (ProcessMyFlaggedEmails) are
                    // independent, so a "flagged only" state (master off, flagged on) is shown as-is — never
                    // force the master on, which would corrupt the user's setting on the next persist.
                    this.settingsState = result ? 'loaded' : 'failed';
                    this.isLoading = false;
                },
                () => {
                    this.settingsState = 'failed';
                    this.isLoading = false;
                }
            );
    }

    private persist(): void {
        this._settingsProxy.updateUserSettings(this.settings).subscribe(
            () => {
                this.toastr.success('Email settings updated', null, this.toastrOptions);
            },
            () => {
                // On failure, re-pull so the toggles reflect the true persisted state, and tell the user.
                this.loadSettings();
                this.toastr.error('Failed to update email settings', null, this.toastrOptions);
            }
        );
    }
}
